import * as validationLib from "../../lib/validation";
import ChevronDown from "../icons/ChevronDown";
import cn from "../../lib/classnames";
import React, {useRef, useState} from "react";
import styles from "./SelectDeposit.module.css";
import textStyles from "../../styles/Text.module.css";

export const EMPTY_VALUE = "";

type Props = React.ComponentProps<"select"> & {
    className?: string;
    extraInfo?: string;
    id: string;
    label: string;
    noEmptyDefault?: boolean;
    options: string[];
    selectedIndex: number;
    setSelected: (index: number, option: string) => void;
    validationMessage?: string[];
};

function SelectDeposit(
    {
        id,
        label,
        options,
        selectedIndex,
        setSelected,
        validationMessage,
        noEmptyDefault = false,
        extraInfo,
        onBlur,
        className,
        ...rest
    }: Props,
    ref: React.ForwardedRef<HTMLSelectElement>,
) {
    const [infoCollapsed, setInfoCollapsed] = useState(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const borderBottomRef = useRef<HTMLDivElement>(null);

    const hasValidationMsg = !!validationMessage && !!validationMessage.length;

    return (
        <div ref={containerRef} className={cn(className, styles.container)}>
            <div className={styles.selectContainer}>
                <select
                    ref={ref}
                    id={id}
                    className={cn(
                        styles.select,
                        textStyles.input,
                        selectedIndex === -1 ? styles.noneSelected : undefined,
                    )}
                    value={selectedIndex}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value !== "") {
                            const index = parseInt(value);
                            setSelected(index, options[index]);
                        }
                    }}
                    onFocus={() => setInfoCollapsed(false)}
                    onBlur={(e) => {
                        setInfoCollapsed(true);
                        if (onBlur) {
                            onBlur(e);
                        }
                    }}
                    {...rest}
                >
                    {label ? (
                        <option value={EMPTY_VALUE} disabled={noEmptyDefault}>
                            {label}
                        </option>
                    ) : null}
                    {options.map((o, i) => (
                        <option key={o} value={i}>
                            {o}
                        </option>
                    ))}
                </select>
                <label
                    className={cn(styles.label, selectedIndex === -1 ? styles.empty : undefined)}
                    htmlFor={id}
                    data-content={label}
                >
                    <span className={styles.hidden}>{label}</span>
                </label>
                <ChevronDown
                    className={cn(
                        styles.chevron,
                        selectedIndex === -1 ? styles.empty : undefined,
                        noEmptyDefault ? styles.rightChevron : undefined,
                    )}
                />
                <div
                    ref={borderBottomRef}
                    className={cn(
                        styles.bottomBorder,
                        hasValidationMsg ? styles.invalid : undefined,
                    )}
                />
            </div>

            {validationMessage !== undefined && validationMessage.length ? (
                <div className={styles.validation}>
                    {validationLib.getDisplayString(validationMessage)}
                </div>
            ) : null}
            {extraInfo ? (
                <div
                    className={cn(
                        textStyles.inputDetail,
                        styles.extraInfo,
                        infoCollapsed ? styles.collapsed : styles.expanded,
                    )}
                >
                    {extraInfo}
                </div>
            ) : null}
        </div>
    );
}

export default React.forwardRef<HTMLSelectElement, Props>(SelectDeposit);
